import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    phone: '',
  });

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    if (cart.length === 0) {
      navigate('/cart');
    }
    setCartItems(cart);
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock order placement
    const order = {
      id: Date.now().toString(),
      items: cartItems,
      customerInfo: formData,
      date: new Date().toISOString(),
      total: subtotal + shipping,
    };

    // Save to mock orders
    const orders = JSON.parse(localStorage.getItem('orders') || '[]');
    orders.push(order);
    localStorage.setItem('orders', JSON.stringify(orders));

    // Clear cart
    localStorage.removeItem('cart');

    toast.success('Order placed successfully!');
    navigate('/');
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 500 ? 0 : 50;

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        <button
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-sm font-bold tracking-wider mb-8 hover:opacity-50 transition-opacity"
        >
          <ArrowLeft size={18} strokeWidth={2.5} />
          BACK TO CART
        </button>

        <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-12">
          CHECKOUT
        </h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Checkout Form */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-black tracking-tighter mb-6">CONTACT</h2>
              <input
                type="email"
                name="email"
                placeholder="Email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
              />
            </div>

            {/* Shipping Information */}
            <div>
              <h2 className="text-2xl font-black tracking-tighter mb-6">SHIPPING</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                  />
                </div>
                <input
                  type="text"
                  name="address"
                  placeholder="Address"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    required
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                  />
                  <input
                    type="text"
                    name="postalCode"
                    placeholder="Postal code"
                    required
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                  />
                </div>
                <input
                  type="text"
                  name="country"
                  placeholder="Country"
                  required
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full border-b-2 border-black py-3 text-sm focus:outline-none focus:border-gray-600"
                />
              </div>
            </div>

            {/* Payment (Mock) */}
            <div>
              <h2 className="text-2xl font-black tracking-tighter mb-6">PAYMENT</h2>
              <div className="bg-gray-50 p-6">
                <p className="text-sm text-gray-600">
                  Payment processing is simulated for this demo.
                </p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-gray-50 p-8 sticky top-32">
              <h2 className="text-2xl font-black tracking-tighter mb-8">ORDER SUMMARY</h2>
              
              {/* Items */}
              <div className="space-y-4 mb-8 max-h-64 overflow-y-auto">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div
                      className="w-16 h-20 flex-shrink-0"
                      style={{ backgroundColor: item.colorBlock }}
                    />
                    <div className="flex-1 text-sm">
                      <p className="font-bold">{item.name}</p>
                      <p className="text-gray-600">Size: {item.selectedSize}</p>
                      <p className="text-gray-600">Qty: {item.quantity}</p>
                      <p className="font-bold mt-1">${item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 border-t border-gray-300 pt-6">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span className="font-bold">${subtotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span className="font-bold">{shipping === 0 ? 'FREE' : `$${shipping}`}</span>
                </div>
                <div className="flex justify-between border-t border-gray-300 pt-3">
                  <span className="font-bold">Total</span>
                  <span className="font-bold text-xl">${subtotal + shipping}</span>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-black text-white py-5 text-sm font-bold tracking-wider hover:bg-gray-800 transition-colors mt-8"
              >
                PLACE ORDER
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
